import { get, writable } from 'svelte/store';
import { router } from '@inertiajs/svelte';
import { undoableAction } from '../undoManager';
import type { Project, ProjectStatus, CalendarEvent } from '../types';
import { MS_PER_DAY, getWeekKey, getWeekStart, parseCalendarDate } from '../utils';
import { persistedWritable } from './persistedWritable';

export { persistedWritable };

// UI State (client-side only, not persisted to server)

// Keyboard shortcuts bar minimized state (persisted)
export const isShortcutsMinimized = persistedWritable('shortcutsMinimized', false);

export function toggleShortcutsBar() {
  isShortcutsMinimized.update((v) => !v);
}

// View mode for month display
export type ViewMode =
  | 'focus'
  | 'rolling'
  | 'timeline'
  | 'year-grid-6x2'
  | 'year-grid-quarters'
  | 'year-table'
  | 'year-focus';
export const viewMode = writable<ViewMode>('rolling');

// Work queue rail (backlog projects and priority tasks)
export type WorkQueueTab = 'backlog' | 'priority';
export const isWorkQueueOpen = writable(false);
export const workQueueTab = persistedWritable<WorkQueueTab>('workQueueTab', 'backlog');

// Status rail (right drawer for viewing all projects in a status)
export interface StatusRailData {
  status: ProjectStatus;
  statusLabel: string;
  monthKey: string;
}
export const isStatusRailOpen = writable(false);
export const statusRailData = writable<StatusRailData | null>(null);

// Drag and drop state
export const draggedProject = writable<Project | null>(null);
export const draggedEvent = writable<string | null>(null);
export const dropTarget = writable<{ monthKey: string; status: string } | null>(null);

// Move confirmation modal (for multi-week projects)
export type MoveType = 'week' | 'month' | 'backlog';
export interface PendingMove {
  projectId: string;
  project: Project;
  moveType: MoveType;
  weekKey?: string;
  monthKey?: string;
  status?: string;
}
export const moveConfirmOpen = writable(false);
export const pendingMove = writable<PendingMove | null>(null);

export function requestProjectMove(
  projectId: string,
  project: Project | null,
  moveType: MoveType,
  opts: { weekKey?: string; monthKey?: string; status?: string },
) {
  if (project && isNoopProjectMove(project, moveType, opts)) return;

  if (project?.multiWeek && moveType !== 'backlog') {
    pendingMove.set({ projectId, project, moveType, ...opts });
    moveConfirmOpen.set(true);
  } else {
    executeProjectMove(projectId, moveType, opts, project);
  }
}

function formatDateKey(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function getProjectDurationDays(project: Project): number {
  if (project.startDate && project.endDate) {
    return Math.round(
      (parseCalendarDate(project.endDate).getTime() -
        parseCalendarDate(project.startDate).getTime()) /
        MS_PER_DAY,
    );
  }
  return 6;
}

function getMonthMoveStart(monthKey: string): Date {
  const firstOfMonth = new Date(
    Date.UTC(parseInt(monthKey.slice(0, 4)), parseInt(monthKey.slice(5, 7)) - 1, 1),
  );
  return new Date(firstOfMonth.getTime() - firstOfMonth.getUTCDay() * MS_PER_DAY);
}

function isSameScheduledRange(project: Project, newStart: Date, newStatus: string): boolean {
  if (project.schedulingState !== 'scheduled' || !project.startDate || !project.endDate)
    return false;

  const durationDays = getProjectDurationDays(project);
  const newEnd = new Date(newStart.getTime() + durationDays * MS_PER_DAY);

  return (
    project.startDate === formatDateKey(newStart) &&
    project.endDate === formatDateKey(newEnd) &&
    project.status === newStatus
  );
}

function isNoopProjectMove(
  project: Project,
  moveType: MoveType,
  opts: { weekKey?: string; monthKey?: string; status?: string },
): boolean {
  if (moveType === 'backlog') return project.schedulingState === 'backlog';

  const newStatus = opts.status || 'planned';

  if (moveType === 'week' && opts.weekKey) {
    return isSameScheduledRange(project, getWeekStart(opts.weekKey), newStatus);
  }

  if (moveType === 'month' && opts.monthKey) {
    return isSameScheduledRange(project, getMonthMoveStart(opts.monthKey), newStatus);
  }

  return false;
}

type RouterPostData = Parameters<typeof router.post>[1];
type RouterPostOptions = Parameters<typeof router.post>[2];

function trackedRouterPost(
  url: string,
  data: RouterPostData,
  options: RouterPostOptions = {},
): Promise<void> {
  return new Promise((resolve, reject) => {
    let settled = false;
    const settle = (fn: () => void) => {
      if (settled) return;
      settled = true;
      fn();
    };

    router.post(url, data, {
      ...options,
      onSuccess: (...args) => {
        options?.onSuccess?.(...args);
        settle(resolve);
      },
      onError: (...args) => {
        options?.onError?.(...args);
        settle(() => reject(new Error('Move failed')));
      },
      onCancel: (...args) => {
        options?.onCancel?.(...args);
        settle(() => reject(new Error('Move cancelled')));
      },
      onFinish: (...args) => {
        options?.onFinish?.(...args);
        settle(resolve);
      },
    });
  });
}

/** Internal: sends the router.post for a move without showing a toast. */
function performProjectMove(
  projectId: string,
  moveType: MoveType,
  opts: { weekKey?: string; monthKey?: string; status?: string },
  tracked = false,
): Promise<void> | void {
  const post = tracked ? trackedRouterPost : router.post;

  if (moveType === 'week' && opts.weekKey) {
    return post(
      `/projects/${projectId}/move_to_week`,
      {
        week_key: opts.weekKey,
        status: opts.status || 'planned',
      },
      { preserveScroll: true },
    );
  } else if (moveType === 'month' && opts.monthKey) {
    return post(
      `/projects/${projectId}/move_to_month`,
      {
        month_key: opts.monthKey,
        status: opts.status || 'planned',
      },
      { preserveScroll: true },
    );
  } else if (moveType === 'backlog') {
    return post(`/projects/${projectId}/move_to_backlog`, {}, { preserveScroll: true });
  }
}

/** Determine the move type and opts needed to return a project to its current location.
 *  Computes weekKey client-side from startDate (the definitive field) rather than
 *  relying on a server-serialized weekKey, avoiding lossy ISO-week round-trip bugs.
 */
function getPreviousMoveInfo(project: Project): {
  moveType: MoveType;
  opts: { weekKey?: string; monthKey?: string; status?: string };
} {
  if (project.schedulingState === 'backlog') {
    return { moveType: 'backlog', opts: {} };
  }
  if (project.startDate) {
    const weekKey = getWeekKey(parseCalendarDate(project.startDate));
    return { moveType: 'week', opts: { weekKey, status: project.status } };
  }
  if (project.monthKey) {
    return { moveType: 'month', opts: { monthKey: project.monthKey, status: project.status } };
  }
  // Fallback — shouldn't happen in practice
  return { moveType: 'backlog', opts: {} };
}

/** Get a human-readable label for the move destination. */
function getMoveDestinationLabel(
  moveType: MoveType,
  _opts: { weekKey?: string; monthKey?: string; status?: string },
): string {
  switch (moveType) {
    case 'backlog':
      return 'Project moved to backlog';
    case 'week':
      return 'Project moved';
    case 'month':
      return 'Project moved';
    default:
      return 'Project moved';
  }
}

export function executeProjectMove(
  projectId: string,
  moveType: MoveType,
  opts: { weekKey?: string; monthKey?: string; status?: string },
  project?: Project | null,
) {
  // Capture previous state before moving (for undo)
  const previousState = project ? getPreviousMoveInfo(project) : null;

  if (previousState) {
    let commitPromise: Promise<void> | null = null;

    undoableAction({
      message: getMoveDestinationLabel(moveType, opts),
      duration: 5000,
      onCommit: () => {
        commitPromise = performProjectMove(projectId, moveType, opts, true) as Promise<void>;
        return commitPromise;
      },
      onUndo: async () => {
        try {
          await commitPromise;
        } catch {
          return;
        }
        await performProjectMove(projectId, previousState.moveType, previousState.opts, true);
      },
    });
  } else {
    performProjectMove(projectId, moveType, opts);
  }
}

export function confirmPendingMove() {
  const move = pendingMoveValue;
  if (move) {
    executeProjectMove(
      move.projectId,
      move.moveType,
      {
        weekKey: move.weekKey,
        monthKey: move.monthKey,
        status: move.status,
      },
      move.project,
    );
  }
  cancelPendingMove();
}

export function cancelPendingMove() {
  moveConfirmOpen.set(false);
  pendingMove.set(null);
}

// Keep a module-level reference for confirmPendingMove
let pendingMoveValue: PendingMove | null = null;
pendingMove.subscribe((v) => {
  pendingMoveValue = v;
});

// Event move confirmation modal (for multi-day events)
export type EventMoveType = 'day' | 'week' | 'month';
export interface PendingEventMove {
  eventId: string;
  event: CalendarEvent;
  moveType: EventMoveType;
  date?: string; // target date for day moves
  weekKey?: string; // target week for week moves
  monthKey?: string; // target month for month moves
  startTime?: string; // target HH:MM for day moves
  forceConfirm?: boolean;
}
export const eventMoveConfirmOpen = writable(false);
export const pendingEventMove = writable<PendingEventMove | null>(null);

let pendingEventMoveValue: PendingEventMove | null = null;
pendingEventMove.subscribe((v) => {
  pendingEventMoveValue = v;
});

export function requestEventMove(
  eventId: string,
  event: CalendarEvent,
  moveType: EventMoveType,
  opts: {
    date?: string;
    weekKey?: string;
    monthKey?: string;
    startTime?: string;
    forceConfirm?: boolean;
  },
) {
  const endDate = event.endDate || event.date;
  const isMultiDay = endDate !== event.date;
  if (isMultiDay || opts.forceConfirm) {
    pendingEventMove.set({ eventId, event, moveType, ...opts });
    eventMoveConfirmOpen.set(true);
  } else {
    executeEventMove(eventId, moveType, opts, event);
  }
}

/** Internal: sends the router.post for an event move without showing a toast. */
function performEventMove(
  eventId: string,
  moveType: EventMoveType,
  opts: { date?: string; weekKey?: string; monthKey?: string; startTime?: string },
  tracked = false,
): Promise<void> | void {
  const post = tracked ? trackedRouterPost : router.post;

  if (moveType === 'day' && opts.date) {
    return post(
      `/calendar_events/${eventId}/move`,
      {
        date: opts.date,
        start_time: opts.startTime,
      },
      { preserveScroll: true },
    );
  } else if (moveType === 'week' && opts.weekKey) {
    return post(
      `/calendar_events/${eventId}/move_to_week`,
      {
        week_key: opts.weekKey,
      },
      { preserveScroll: true },
    );
  } else if (moveType === 'month' && opts.monthKey) {
    return post(
      `/calendar_events/${eventId}/move`,
      {
        date: `${opts.monthKey}-01`,
      },
      { preserveScroll: true },
    );
  }
}

export function executeEventMove(
  eventId: string,
  moveType: EventMoveType,
  opts: { date?: string; weekKey?: string; monthKey?: string; startTime?: string },
  event?: CalendarEvent | null,
) {
  // Capture previous state before moving (for undo)
  const previousDate = event?.date ?? null;

  if (previousDate) {
    let commitPromise: Promise<void> | null = null;

    undoableAction({
      message: 'Event moved',
      duration: 5000,
      onCommit: () => {
        commitPromise = performEventMove(eventId, moveType, opts, true) as Promise<void>;
        return commitPromise;
      },
      onUndo: async () => {
        try {
          await commitPromise;
        } catch {
          return;
        }
        await performEventMove(eventId, 'day', { date: previousDate }, true);
      },
    });
  } else {
    performEventMove(eventId, moveType, opts);
  }
}

export function confirmPendingEventMove() {
  const move = pendingEventMoveValue;
  if (move) {
    executeEventMove(
      move.eventId,
      move.moveType,
      {
        date: move.date,
        weekKey: move.weekKey,
        monthKey: move.monthKey,
        startTime: move.startTime,
      },
      move.event,
    );
  }
  cancelPendingEventMove();
}

export function cancelPendingEventMove() {
  eventMoveConfirmOpen.set(false);
  pendingEventMove.set(null);
}

// Event modal state
export const eventModalOpen = writable(false);
export const eventModalDate = writable<string | null>(null);
export const eventModalStartTime = writable<string | null>(null);
export const eventModalEvent = writable<CalendarEvent | null>(null);

// Project modal state
export interface ProjectModalContext {
  schedulingState: 'backlog' | 'scheduled';
  monthKey?: string;
  targetDate?: string;
  status?: ProjectStatus;
  durationWeeks?: number;
  mode?: 'full' | 'schedule';
  initialTitle?: string;
  initialNotes?: string;
  onCreated?: () => void;
}
export const projectModalOpen = writable(false);
export const projectModalProject = writable<Project | null>(null);
export const projectModalContext = writable<ProjectModalContext | null>(null);

// Broadcast project edits to canvas views that keep their own node data cache.
export const projectUpdated = writable<Project | null>(null);

export function notifyProjectUpdated(project: Project) {
  // `writable` notifies current subscribers synchronously, then clear the value
  // so a later board mount cannot replay an update from an earlier visit.
  projectUpdated.set(project);
  projectUpdated.set(null);
}

// Work queue controls
export function openWorkQueue(tab: WorkQueueTab = 'backlog') {
  workQueueTab.set(tab);
  // Keep the queue mutually exclusive with other right rails.
  closeProjectRail();
  closeStatusRail();
  closeMonthRail();
  closeEventsRail();
  isWorkQueueOpen.set(true);
}

export function closeWorkQueue() {
  isWorkQueueOpen.set(false);
}

export function toggleWorkQueue() {
  let currentlyOpen = false;
  isWorkQueueOpen.subscribe((v) => (currentlyOpen = v))();
  if (currentlyOpen) {
    closeWorkQueue();
  } else {
    openWorkQueue(get(workQueueTab));
  }
}

export function openEventModal(date?: string, startTime?: string) {
  eventModalEvent.set(null);
  eventModalDate.set(date || null);
  eventModalStartTime.set(startTime || null);
  eventModalOpen.set(true);
}

export function openEventModalForEdit(event: CalendarEvent) {
  eventModalEvent.set(event);
  eventModalDate.set(event.date);
  eventModalStartTime.set(null);
  eventModalOpen.set(true);
}

export function closeEventModal() {
  eventModalOpen.set(false);
  eventModalDate.set(null);
  eventModalStartTime.set(null);
  eventModalEvent.set(null);
}

// Open project modal for creating a new project
export function openProjectModalForCreate(context: ProjectModalContext) {
  projectModalProject.set(null);
  projectModalContext.set(context);
  projectModalOpen.set(true);
}

// Open project modal for editing an existing project
export function openProjectModalForEdit(project: Project) {
  projectModalProject.set(project);
  projectModalContext.set({
    schedulingState: project.schedulingState,
    monthKey: project.monthKey || undefined,
    status: project.status,
  });
  projectModalOpen.set(true);
}

// Open project modal for creating a new project from an existing task's title/notes.
// onCreated fires only once the project is actually saved, so callers can safely
// delete the source task on success (e.g. via undoableSoftDelete).
export function openProjectModalForConvertFromTask(
  title: string,
  notes: string,
  onCreated?: () => void,
) {
  projectModalProject.set(null);
  projectModalContext.set({
    schedulingState: 'backlog',
    initialTitle: title,
    initialNotes: notes,
    onCreated,
  });
  projectModalOpen.set(true);
}

export function openProjectScheduleModal(project: Project) {
  projectModalProject.set(project);
  projectModalContext.set({
    schedulingState: project.schedulingState,
    monthKey: project.monthKey || undefined,
    status: project.status,
    mode: 'schedule',
  });
  projectModalOpen.set(true);
}

export function closeProjectModal() {
  projectModalOpen.set(false);
  projectModalProject.set(null);
  projectModalContext.set(null);
}

// Open status rail to show all projects for a status
export function openStatusRail(data: StatusRailData) {
  statusRailData.set(data);
  isStatusRailOpen.set(true);
  // Close other rails
  closeProjectRail();
  isMonthRailOpen.set(false);
  isEventsRailOpen.set(false);
  closeWorkQueue();
}

export function closeStatusRail() {
  isStatusRailOpen.set(false);
  statusRailData.set(null);
}

// Month rail (right drawer for viewing all projects in a month/week, grouped by status)
export interface MonthRailData {
  monthKey: string;
  monthLabel: string;
  // Optional: when viewing a week's projects
  weekKey?: string;
}
export const isMonthRailOpen = writable(false);
export const monthRailData = writable<MonthRailData | null>(null);

export function openMonthRail(data: MonthRailData) {
  monthRailData.set(data);
  isMonthRailOpen.set(true);
  // Close other rails
  closeProjectRail();
  isStatusRailOpen.set(false);
  isEventsRailOpen.set(false);
  closeWorkQueue();
}

export function openMonthRailForWeek(data: { weekKey: string; weekLabel: string }) {
  monthRailData.set({
    monthKey: '', // Not used for week mode
    monthLabel: data.weekLabel,
    weekKey: data.weekKey,
  });
  isMonthRailOpen.set(true);
  // Close other rails
  closeProjectRail();
  isStatusRailOpen.set(false);
  isEventsRailOpen.set(false);
  closeWorkQueue();
}

export function closeMonthRail() {
  isMonthRailOpen.set(false);
  monthRailData.set(null);
}

// Events rail (right drawer for viewing events)
// Supports different period types: day, week, month
export type EventsPeriodType = 'day' | 'week' | 'month';
export interface EventsRailData {
  periodType: EventsPeriodType;
  periodKey: string; // date string for day, week key for week, month key for month
  periodLabel: string;
  // Legacy support - kept for backward compatibility
  monthKey?: string;
  monthLabel?: string;
}
export const isEventsRailOpen = writable(false);
export const eventsRailData = writable<EventsRailData | null>(null);

export function openEventsRail(data: EventsRailData) {
  eventsRailData.set(data);
  isEventsRailOpen.set(true);
  // Close other rails
  closeProjectRail();
  isStatusRailOpen.set(false);
  isMonthRailOpen.set(false);
  closeWorkQueue();
}

// Convenience function for opening events for a specific day
export function openEventsRailForDay(date: string, label: string) {
  openEventsRail({
    periodType: 'day',
    periodKey: date,
    periodLabel: label,
  });
}

// Convenience function for opening events for a week
export function openEventsRailForWeek(weekKey: string, label: string) {
  openEventsRail({
    periodType: 'week',
    periodKey: weekKey,
    periodLabel: label,
  });
}

// Convenience function for opening events for a month
export function openEventsRailForMonth(monthKey: string, label: string) {
  openEventsRail({
    periodType: 'month',
    periodKey: monthKey,
    periodLabel: label,
    // Legacy support
    monthKey: monthKey,
    monthLabel: label,
  });
}

export function closeEventsRail() {
  isEventsRailOpen.set(false);
  eventsRailData.set(null);
}

// Project rail (right slide-over for viewing project + tasks)
export interface ProjectRailData {
  projectId: string;
}
export const isProjectRailOpen = writable(false);
export const projectRailData = writable<ProjectRailData | null>(null);

export function openProjectRail(projectId: string) {
  projectRailData.set({ projectId });
  isProjectRailOpen.set(true);
  // Close other rails
  isStatusRailOpen.set(false);
  isMonthRailOpen.set(false);
  isEventsRailOpen.set(false);
  closeWorkQueue();
}

export function closeProjectRail() {
  isProjectRailOpen.set(false);
  projectRailData.set(null);
}

// Close all right rails (used by MobileSheet dismiss on mobile)
export function closeAllRightRails() {
  closeProjectRail();
  closeStatusRail();
  closeMonthRail();
  closeEventsRail();
  closeWorkQueue();
}

// Sign-out confirmation modal
export const signOutModalOpen = writable(false);

export function openSignOutModal() {
  signOutModalOpen.set(true);
}

export function closeSignOutModal() {
  signOutModalOpen.set(false);
}
